import { type ReactNode, useEffect, useId, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import { FaChevronDown, FaClock, FaUserFriends } from "react-icons/fa";
import { CREATE_GAME_TAB, DAILY_GAMES_TAB } from "@chesshotel/constants";
import { useLobby } from "@/redux/lobby";
import { useWsConnection } from "@/redux/wsConnection";
import CreateGame from "./CreateGame";
import JoinGame from "./JoinGame";
import CActive from "./daily/CActive";
import PlayersOnline from "./PlayersOnline";
import LeagueCardContainer from "./infoCards/LeagueCardContainer";
import PracticeCardContainer from "./infoCards/PracticeCardContainer";

export default function MobileLobby({
  gamesCount,
  myTurnGames,
  mobileAd,
}: {
  gamesCount: number;
  myTurnGames: number;
  mobileAd: ReactNode;
}) {
  const { t } = useTranslation("common");
  const { t: lobbyT } = useTranslation("lobby");
  const { invites, tab, leaguePlay } = useLobby();
  const { wsConnected, otherLoginShow } = useWsConnection();
  const hasGames = gamesCount > 0;
  const id = useId();
  const [expanded, setExpanded] = useState<"invites" | "games" | null>(
    tab === DAILY_GAMES_TAB ? "games" : null,
  );

  useEffect(() => {
    if (tab === DAILY_GAMES_TAB && hasGames) setExpanded("games");
    if (tab === CREATE_GAME_TAB || !hasGames) {
      setExpanded((current) => (current === "games" ? null : current));
    }
  }, [tab, hasGames]);

  const sections = [
    {
      key: "invites" as const,
      label: lobbyT("Join game"),
      Icon: FaUserFriends,
      count: invites.length,
    },
    ...(hasGames
      ? [
          {
            key: "games" as const,
            label: t("Games"),
            Icon: FaClock,
            count: gamesCount,
          },
        ]
      : []),
  ];

  return (
    <div className="flex w-full min-w-0 flex-1 flex-col gap-3">
      {leaguePlay ? (
        <LeagueCardContainer isDesktop={false} />
      ) : (
        <PracticeCardContainer />
      )}
      {sections.map(({ key, label, Icon, count }) => {
        const open = expanded === key;
        return (
          <div key={key} className="overflow-hidden rounded-xl bg-white">
            <button
              type="button"
              id={`${id}-${key}-button`}
              aria-expanded={open}
              aria-controls={`${id}-${key}-content`}
              onClick={() => setExpanded(open ? null : key)}
              className="flex min-h-12 w-full items-center gap-3 bg-white px-4 py-3 text-left text-sm font-semibold text-gray-800 transition-colors active:bg-athens-gray-50 md:hover:bg-athens-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            >
              <Icon
                className="shrink-0 text-base text-altblue-700"
                aria-hidden="true"
              />
              <span className="flex-1">{label}</span>
              {key === "games" && myTurnGames > 0 && (
                <span className="text-xs font-normal text-altblue-700">
                  {t("Your move")} {myTurnGames}
                </span>
              )}
              <span
                className={`min-w-6 rounded-md px-1.5 py-0.5 text-center text-xs tabular-nums transition-colors ${
                  open
                    ? "bg-altblue-500 text-white"
                    : "bg-athens-gray-100 text-gray-600"
                }`}
              >
                {count}
              </span>
              <FaChevronDown
                className={`shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>
            <div
              id={`${id}-${key}-content`}
              role="region"
              aria-labelledby={`${id}-${key}-button`}
              hidden={!open}
              className="border-t border-athens-gray-150"
            >
              {open &&
                (otherLoginShow ? (
                  <div className="bg-athens-gray-50 px-4 py-6 text-center text-sm text-altgray-600">
                    {t("Live updates paused")}
                  </div>
                ) : key === "invites" ? (
                  <div className="h-[min(360px,60svh)] overflow-y-auto bg-white">
                    <JoinGame compactMobile />
                  </div>
                ) : !wsConnected ? (
                  <div className="flex justify-center bg-athens-gray-50 p-6">
                    <span className="loader" />
                  </div>
                ) : (
                  <CActive />
                ))}
            </div>
          </div>
        );
      })}
      <div className="mt-auto flex shrink-0 flex-col pt-3">
        {!otherLoginShow && (
          <div className="pb-3">
            <PlayersOnline />
          </div>
        )}
        <CreateGame isDesktop={false} showBanner={false} timePopover />
        {mobileAd}
      </div>
    </div>
  );
}
